import React from "react";
import {
  Button,
  Collapse,
  Space,
  Popconfirm,
  Tag,
  Row,
  Col,
  Select,
  Popover,
} from "antd";
import {
  EditFilled,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import QuantityStepper from "../../../../components/QuantityStepper";
import CurrencyFormatter from "../../../../components/CurrencyFormatter";
import TaxCalculator from "../../../../components/TaxCalculator";
import AddOrder from "./AddOrder";
import {
  getEntityBranchId,
  isBranchScopedWaiter,
  isEntityInBranch,
} from "../../../../utils/branchScope";

const CartPanel = ({
  t,
  appConfig,
  fullScreenPOS,
  setFullScreenPOS,
  cart,
  authUser,
  onEditPrice,
  onCartAction,
  is_stock_management_active,
  roundOff,
  totalTax,
  totalAmount,
  is_table_management_active,
  tablesCount,
  diningTablesData,
  onDiningTableChange,
  is_waiter_management_active,
  is_job_management_active,
  jobPersonsCount,
  employeesData,
  onWaiterChange,
  loginUserIsWaiter,
  diningTableId,
  waiterId,
  is_order_management_active,
  setCart,
  onCheckout,
  onCheckoutTakeaway,
}) => {
  const decrementProduct = (product) => onCartAction(product, "dec");

  const getCartProductQuantity = (productId) =>
    cart
      .filter((item) => Number(item.id) === Number(productId))
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const isIncreaseDisabled = (product) =>
    is_stock_management_active &&
    product.is_stockable &&
    Number(product.stock || 0) <= getCartProductQuantity(product.id);

  const currentBranchId = getEntityBranchId(authUser);

  const diningTableOptions = diningTablesData
    .filter((table) => isEntityInBranch(table, currentBranchId))
    .map((table) => ({
      label: table.name,
      value: table.id,
    }));

  const branchWaiters = employeesData.filter((employee) =>
    isBranchScopedWaiter(employee, currentBranchId),
  );

  const selectedWaiter = branchWaiters.find(
    (employee) => String(employee.id) === String(waiterId),
  );

  const waiterOptions = branchWaiters.map((employee) => ({
    label: employee.name,
    value: employee.id,
  }));

  return (
    <div
      className="pos-cart-panel"
      style={{
        position: "fixed",
        right: appConfig.direction === "ltr" ? 0 : "unset",
        left: appConfig.direction === "ltr" ? "unset" : 0,
        top: fullScreenPOS ? 0 : 65,
        height: fullScreenPOS ? "100dvh" : "calc(100dvh - 65px)",
        boxShadow: "var(--color-overlay-strong) 0px 0px 30px -10px",
        width: "400px",
      }}
    >
      <div className="pos-cart-main">
        <div className="pos-cart-header">
          <h2 style={{ margin: 0, fontWeight: "bold" }}>{t("cart")}</h2>

          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              type="primary"
              onClick={() => setFullScreenPOS(!fullScreenPOS)}
              style={{ height: "auto", padding: "10px" }}
            >
              {fullScreenPOS ? (
                <FullscreenExitOutlined
                  style={{ fontSize: "20px", display: "flex" }}
                />
              ) : (
                <FullscreenOutlined
                  style={{ fontSize: "20px", display: "flex" }}
                />
              )}
            </Button>
          </div>
        </div>

        <div className="cart_card">
          {cart.length < 1 ? (
            <div className="app-text-center">{t("cart_empty")}</div>
          ) : (
            <Collapse
              defaultActiveKey={["1"]}
              items={cart.map((product, i) => {
                let subTotal =
                  (Number(product.price) || 0) *
                  (Number(product.quantity) || 0);
                let taxPercentage = 0;

                if (product.is_taxable === true) {
                  product.taxes?.forEach((tax) => {
                    taxPercentage += Number(tax.rate);
                  });
                  subTotal += subTotal * (taxPercentage / 100);
                }

                const collapseKey = `product-${product.cart_key || product.id}-${i}`;

                return {
                  key: collapseKey,
                  label: (
                    <Space>
                      <Popconfirm
                        title={t("warning_remove_product")}
                        onConfirm={() => onCartAction(product, "remove")}
                        okText={t("yes")}
                        cancelText={t("no")}
                        placement="topLeft"
                      >
                        <DeleteOutlined
                          className="pos-cart-remove-icon"
                          title={t("delete_entity")}
                        />
                      </Popconfirm>

                      <strong dir="ltr">
                        {product.quantity} x{" "}
                        <bdi dir="auto">
                          {appConfig.direction === "rtl"
                            ? product.name
                            : product.name_en}
                        </bdi>
                      </strong>
                    </Space>
                  ),
                  extra: (
                    <span className="brand-color">
                      <CurrencyFormatter value={product.price} />

                      {authUser?.allow_editable_price === true && (
                        <EditFilled
                          style={{
                            color: "var(--color-primary-hover)",
                            marginLeft:
                              appConfig.direction === "ltr" ? "8px" : "0",
                            marginRight:
                              appConfig.direction === "ltr" ? "0" : "8px",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEditPrice(product.cart_key);
                          }}
                        />
                      )}
                    </span>
                  ),
                  children: (
                    <div className="pos-cart-item-details">
                      <div className="pos-cart-quantity-row">
                        <span>{t("quantity")}:</span>
                        <QuantityStepper
                          value={product.quantity}
                          increaseDisabled={isIncreaseDisabled(product)}
                          className="pos-cart-quantity-stepper"
                          onDecrease={() => decrementProduct(product)}
                          onIncrease={() => onCartAction(product, "inc")}
                        />
                      </div>

                      {product.tags && product.tags.length > 0 && (
                        <div style={{ marginTop: "5px" }}>
                          <div style={{ marginBottom: 6, fontWeight: 600 }}>
                            {t("options")}:
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {Array.isArray(product.selectedTagIds) &&
                              product.selectedTagIds.length > 0 &&
                              product.selectedTagIds.map((tid) => {
                                const tagObj = product.tags.find(
                                  (x) => Number(x.id) === Number(tid),
                                );

                                const tagLabel = tagObj
                                  ? appConfig.direction === "rtl"
                                    ? tagObj.name_ar || tagObj.name
                                    : tagObj.name
                                  : tid;

                                return (
                                  <Tag
                                    key={String(tid)}
                                    style={{ marginBottom: 6 }}
                                  >
                                    {tagLabel}
                                  </Tag>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      {product.is_taxable && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: "10px",
                            flexWrap: "wrap",
                          }}
                        >
                          {t("tax")}:
                          {product.taxes?.map((tax, ii) => (
                            <Tag color="orange" key={ii}>
                              {tax.title} : {tax.rate}%
                            </Tag>
                          ))}
                        </div>
                      )}

                      <div
                        className="app-flex-align-center-gap-10"
                      >
                        {t("subtotal")}:
                        <span className="brand-color">
                          <CurrencyFormatter value={roundOff(subTotal)} />
                        </span>
                      </div>
                    </div>
                  ),
                };
              })}
            />
          )}
        </div>
      </div>

      {cart.length !== 0 && (
        <div className="pos-cart-footer">
          <div className="pos-cart-footer-content">
            <Row className="pos-cart-fields-row" gutter={[10, 12]}>
              {is_table_management_active && tablesCount.length > 0 && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                  <b>{t("dining_table")}</b>
                  <Select
                    showSearch
                    allowClear
                    style={{ width: "100%", height: "40px", paddingTop: "5px" }}
                    onChange={(id) => onDiningTableChange(id || 0)}
                    value={diningTableId || undefined}
                    options={diningTableOptions}
                    filterOption={(input, option) =>
                      String(option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    size="large"
                  />
                </Col>
              )}

              {(is_waiter_management_active || is_job_management_active) &&
                jobPersonsCount.length > 0 && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                    <b>
                      {is_waiter_management_active
                        ? t("select_waiter")
                        : t("select_job_person")}
                    </b>
                    <Select
                      showSearch
                      allowClear={!loginUserIsWaiter}
                      style={{ width: "100%", height: "40px", paddingTop: "5px" }}
                      onChange={(id) => onWaiterChange(id || 0)}
                      value={waiterId || undefined}
                      disabled={loginUserIsWaiter}
                      options={waiterOptions}
                      filterOption={(input, option) =>
                        String(option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      size="large"
                    />
                  </Col>
                )}
            </Row>

            <h4
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                columnGap: "10px",
                paddingTop: "10px",
                margin: 0,
              }}
            >
              <span>
                {t("taxes")}
                <Popover content={<TaxCalculator data={cart} />} trigger="click">
                  <InfoCircleOutlined
                    className="app-info-icon"
                  />
                </Popover>
              </span>
              <strong>
                <CurrencyFormatter value={totalTax} />
              </strong>
            </h4>

            <h4
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                columnGap: "10px",
              }}
            >
              <span>{t("total_amount")}:</span>
              <strong>
                <CurrencyFormatter value={totalAmount} />
              </strong>
            </h4>
          </div>

          <div className="pos-cart-actions">
            {is_order_management_active && (
              <AddOrder
                diningTableId={diningTableId > 0 ? diningTableId : null}
                waiterId={waiterId > 0 ? waiterId : null}
                waiterBranchId={getEntityBranchId(selectedWaiter)}
                branchId={currentBranchId}
                items={cart}
                reloadCallback={() => setCart([])}
                onTakeawayCheckout={onCheckoutTakeaway}
              />
            )}

            <Button
              type="primary"
              size="large"
              style={{ textAlign: "center", fontWeight: "bold", width: "100%" }}
              onClick={onCheckout}
            >
              {t("checkout")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPanel;
